import React from 'react';
import { Alert, InputNumber, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatMoneyPl } from '../utils/currency';
import { formatLocalizedPercent } from '../utils/numberFormatting';
import { formatUnit } from '../utils/units';
import { formatDate } from '../utils/dateUtils';
import {
  hasChanged,
  toPLN,
  type CostSplitPreviewRow,
} from '../modules/transports/transportCostSplitPreview';
import type { SharedTransportPreviewRow } from './sharedTransportFormUtils';
import './TransportCostSplitPreview.css';

const { Text } = Typography;

export interface TransportPreviewRate {
  /** PLN for 1 unit of the truck currency (1 for PLN). */
  rate: number;
  /** NBP table date; null for PLN or when the snapshot carries none. */
  effectiveDate: string | null;
  /** The FX snapshot already saved on this truck (kept by the save). */
  saved?: boolean;
}

interface TransportCostSplitPreviewProps {
  direction: 'INBOUND' | 'OUTBOUND';
  currency: string;
  transportCost: number | null;
  /** INBOUND: per ZD line. */
  inboundRows: CostSplitPreviewRow[];
  /** OUTBOUND: per ZK (buildAllocationPreview). */
  outboundRows: SharedTransportPreviewRow[];
  /** Saved per-ZK freight in PLN (edit mode) for „było → jest”. */
  savedOutboundPLN: Record<string, number | null>;
  /** Goods value key: the shares come from the server on save. */
  valueComputedOnSave: boolean;
  /** Manual key: Σ entered − cost (0 = matches); null = not the manual key. */
  manualDifference: number | null;
  /** The manual amounts were prefilled from the previous split. */
  manualPrefilled?: boolean;
  /**
   * Round 2 (U9): manual key on an OUTBOUND truck — the amount per ZK is
   * typed right here (spread over its cargo lines by the form).
   */
  manualByOrderId?: Record<string, number | null>;
  onManualOrderAmount?: (orderId: string, amount: number | null) => void;
  /**
   * Positions that really get 0 under the chosen key (one warning, listing
   * them; never for a single load). Usability round 2026-10-05.
   */
  zeroNames?: string[];
  rate: TransportPreviewRate | null;
  rateLoading: boolean;
  /**
   * „Było → jest” only when the PLN figures use the same rate as the saved
   * ones (PLN truck or the saved snapshot); else it would show a rate move.
   */
  compareWithSaved?: boolean;
}

/**
 * „Podział kosztu auta” — live preview of the split while the operator edits
 * (decision 2026-10-04, F2). Display only; the saved split is the server's.
 */
const TransportCostSplitPreview: React.FC<TransportCostSplitPreviewProps> = ({
  direction,
  currency,
  transportCost,
  inboundRows,
  outboundRows,
  savedOutboundPLN,
  valueComputedOnSave,
  manualDifference,
  manualPrefilled = false,
  manualByOrderId,
  onManualOrderAmount,
  zeroNames = [],
  rate,
  rateLoading,
  compareWithSaved = true,
}) => {
  const { t, i18n } = useTranslation(['transport']);
  const isPLN = currency === 'PLN';
  const money = (value: number | null | undefined, code = currency) => (
    value === null || value === undefined ? '—' : formatMoneyPl(value, code)
  );
  const share = (value: number | null) => (value === null ? '—' : formatLocalizedPercent(value, i18n.language, 2));
  const beforeAfter = (savedPLN: number | null | undefined, nextPLN: number | null) => (
    compareWithSaved && hasChanged(savedPLN, nextPLN) ? (
      <Text type="warning" data-testid="transport-cost-split-changed">
        {t('transport:costSplit.changed', { before: money(savedPLN, 'PLN'), after: money(nextPLN, 'PLN') })}
      </Text>
    ) : null
  );
  // A typed 0 is a price: no „Wpisz koszt auta” for it.
  const hasCost = transportCost !== null && Number.isFinite(transportCost) && transportCost >= 0;
  const rows = direction === 'INBOUND' ? inboundRows : outboundRows;

  // Owner 2026-10-10: one row per position with a share bar (no wide table).
  const bar = (percent: number | null) => (
    <span className="transport-cost-split__bar" aria-hidden="true">
      <span style={{ width: `${Math.max(0, Math.min(100, percent ?? 0))}%` }} />
    </span>
  );

  const inboundRow = (row: CostSplitPreviewRow) => (
    <li key={row.key} className="transport-cost-split__row">
      <span className="transport-cost-split__what">
        <span className="transport-cost-split__name">{row.name}</span>
        {row.dimensions ? (
          <span className="transport-cost-split__sub" data-testid={`transport-cost-split-dims-${row.key}`}>{row.dimensions}</span>
        ) : null}
        <span className="transport-cost-split__sub">
          {[row.orderLabel, row.valuePLN !== null && row.valuePLN !== undefined
            ? t('transport:costSplit.valueOf', { value: money(row.valuePLN, 'PLN') })
            : null].filter(Boolean).join(' · ')}
        </span>
      </span>
      <span className="transport-cost-split__share">
        {bar(row.sharePercent)}
        <span className="transport-cost-split__percent">{share(row.sharePercent)}</span>
      </span>
      <span className="transport-cost-split__money">
        <span className="transport-cost-split__amount" data-testid="transport-cost-split-amount">{money(row.amount)}</span>
        {!isPLN && row.amountPLN !== null ? <Text type="secondary" className="transport-cost-split__sub">{money(row.amountPLN, 'PLN')}</Text> : null}
        {row.perUnit ? (
          <span className="transport-cost-split__sub" data-testid="transport-cost-split-per-unit">
            {t('transport:costSplit.perUnitValue', {
              amount: money(row.perUnit.amount),
              unit: formatUnit(row.perUnit.unit, i18n.language),
            })}
            {!isPLN && row.perUnit.amountPLN !== null ? ` · ${money(row.perUnit.amountPLN, 'PLN')}` : null}
          </span>
        ) : null}
        {beforeAfter(row.savedPLN, row.amountPLN)}
      </span>
    </li>
  );

  const outboundRow = (row: SharedTransportPreviewRow) => {
    const nextPLN = valueComputedOnSave
      ? row.allocatedTransportCost
      : toPLN(row.allocatedTransportCost, rate?.rate ?? null);
    return (
      <li key={row.orderId} className="transport-cost-split__row">
        <span className="transport-cost-split__what">
          <span className="transport-cost-split__name">{row.customerName}</span>
          <span className="transport-cost-split__sub">{`ZK ${row.displayOrderNumber}`}</span>
        </span>
        <span className="transport-cost-split__share">
          {bar(row.allocationPercent)}
          <span className="transport-cost-split__percent">
            {row.allocationPercent === null ? t('transport:allocationKey.computedOnSave') : share(row.allocationPercent)}
          </span>
        </span>
        <span className="transport-cost-split__money">
          {onManualOrderAmount && manualByOrderId ? (
            <InputNumber
              value={manualByOrderId[row.orderId] ?? null}
              min={0}
              precision={2}
              addonAfter={currency}
              decimalSeparator=","
              style={{ width: 170 }}
              aria-label={t('transport:costSplit.manualAmountFor', { order: row.displayOrderNumber })}
              data-testid="transport-cost-split-manual-input"
              onChange={(value) => onManualOrderAmount(row.orderId, value === null || value === undefined ? null : Number(value))}
            />
          ) : (
            <>
              <span className="transport-cost-split__amount" data-testid="transport-cost-split-amount">
                {valueComputedOnSave ? money(row.allocatedTransportCost, 'PLN') : money(row.allocatedTransportCost)}
              </span>
              {!valueComputedOnSave && !isPLN && nextPLN !== null ? <Text type="secondary" className="transport-cost-split__sub">{money(nextPLN, 'PLN')}</Text> : null}
              {beforeAfter(savedOutboundPLN[row.orderId], nextPLN)}
            </>
          )}
        </span>
      </li>
    );
  };

  return (
    <section className="transport-cost-split-preview" data-testid="transport-cost-split-preview">
      <div className="transport-cost-split__title">{t('transport:costSplit.title')}</div>
      <Text type="secondary" className="transport-cost-split__hint">
        {direction === 'INBOUND' ? t('transport:costSplit.inboundHint') : t('transport:costSplit.outboundHint')}
      </Text>
      {!hasCost ? (
        <Alert type="info" showIcon message={t('transport:costSplit.noCost')} style={{ marginBottom: 8 }} />
      ) : null}
      {zeroNames.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          data-testid="transport-cost-split-zero-warning"
          message={t('transport:costSplit.zeroPositions', { names: zeroNames.join(', ') })}
          style={{ marginBottom: 8 }}
        />
      ) : null}
      {valueComputedOnSave ? (
        <Alert type="info" showIcon message={t('transport:costSplit.valueOnSave')} style={{ marginBottom: 8 }} />
      ) : null}
      {manualPrefilled && manualDifference !== null ? (
        <Alert type="info" showIcon message={t('transport:costSplit.manualPrefilled')} style={{ marginBottom: 8 }} />
      ) : null}
      {manualDifference !== null && hasCost ? (
        <Alert
          type={manualDifference === 0 ? 'success' : 'warning'}
          showIcon
          data-testid="transport-cost-split-manual-check"
          // Usability round 2026-10-05: say live what is left to split (or
          // how much is too much), not only that the sum differs.
          message={manualDifference === 0
            ? t('transport:costSplit.manualMatchesAmounts', { cost: money(transportCost) })
            : manualDifference < 0
              ? t('transport:costSplit.manualLeft', {
                left: money(-manualDifference),
                sum: money((transportCost ?? 0) + manualDifference),
                cost: money(transportCost),
              })
              : t('transport:costSplit.manualOver', {
                over: money(manualDifference),
                sum: money((transportCost ?? 0) + manualDifference),
                cost: money(transportCost),
              })}
          style={{ marginBottom: 8 }}
        />
      ) : null}
      {rows.length === 0 ? (
        <Text type="secondary">{t('transport:costSplit.empty')}</Text>
      ) : (
        <ul className="transport-cost-split__list">
          {direction === 'INBOUND' ? inboundRows.map(inboundRow) : outboundRows.map(outboundRow)}
        </ul>
      )}
      {!isPLN ? (
        <Text type="secondary" style={{ display: 'block', marginTop: 6 }} data-testid="transport-cost-split-rate">
          {rate?.saved
            ? t('transport:costSplit.fxSavedRate', {
              currency,
              rate: rate.rate.toLocaleString('pl-PL', { maximumFractionDigits: 4 }),
            })
            : rate
            ? t('transport:costSplit.fxRate', {
              currency,
              rate: rate.rate.toLocaleString('pl-PL', { maximumFractionDigits: 4 }),
              date: rate.effectiveDate ? formatDate(rate.effectiveDate) : '—',
            })
            : rateLoading ? t('transport:costSplit.fxLoading') : t('transport:costSplit.fxUnavailable')}
        </Text>
      ) : null}
    </section>
  );
};

export default TransportCostSplitPreview;
